import React from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import Icon from 'view/Icon';
import { Background, Border, Main, Shade, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { TEXT_LABELS } from 'constants/order';

const FooterCard = styled(FlexboxContainer) <any>`
  border: 1px solid ${Border.light};
  border-radius: ${BorderRadius.m};
  background-color: ${Background.white};
  box-sizing: border-box;
  padding: ${Space.sp2x};
  margin: 0 ${Space.sp2x} ${Space.sp2x};
`;

const ActionHint = styled.span`
  color: ${Text.secondary};
  font-size: 12px;
  line-height: 18px;
  margin-top: ${Space.spHalf};
  text-align: center;
  white-space: nowrap;
`;

const BuyerNote = styled.span`
  color: ${Text.secondary};
  font-size: 12px;
  line-height: 18px;
  max-width: 120px;
  text-align: left;
`;

const NoteIcon = styled(Icon).attrs({ name: 'info', size: 16 })`
  color: ${Text.secondary};
  flex-shrink: 0;
  margin-top: 1px;
`;

interface Props {
  onCancel: () => void;
  onApplyChanges: () => void;
  isApplying?: boolean;
  /** True once changes have been applied (snapshot SUCCESS) — flips the footer actions. */
  hasAppliedChanges?: boolean;
  onSendForApproval?: () => void;
  /** True while the send-for-approval state transition request is in flight. */
  isSending?: boolean;
  hasUnsavedChanges?: boolean;
  /** True while any row has a validation error on screen — blocks "Apply changes". */
  hasInvalidEdits?: boolean;
  isCancelling?: boolean;
};

/**
 * Page level actions of the Edit order screen. Once changes are applied successfully
 * (snapshot editOrderResult = SUCCESS), "Apply changes" is disabled and "Send for
 * approval" becomes the primary action. Making further edits flips them back - the new
 * changes must be applied before the order can be sent for approval.
 */
const EditOrderFooter = ({ onCancel, onApplyChanges, isApplying, hasAppliedChanges, onSendForApproval, isSending, hasUnsavedChanges, hasInvalidEdits, isCancelling }: Props) => {
  // Ready to send only when the last apply succeeded AND nothing new is pending
  const canSendForApproval = !!hasAppliedChanges && !hasUnsavedChanges;

  return (
    <FooterCard justifyContent="space-between" alignItems="flex-start">
      <FlexboxContainer gap={Space.spBasePlus} alignItems="flex-start">
        <Button
          isGhost
          theme="error"
          color={Main.error}
          borderColor={Main.error}
          onClick={onCancel}
          disabled={isCancelling}
        >
          {TEXT_LABELS.CANCEL}
        </Button>
        <FlexboxContainer flexDirection="column" alignItems="flex-start">
          <Button
            isGhost
            theme="brandDark"
            color={Main.brandDark}
            borderColor={Main.brandDark}
            onClick={onApplyChanges}
            disabled={isApplying || !hasUnsavedChanges || !!hasInvalidEdits}
          >
            {TEXT_LABELS.APPLY_CHANGES}
          </Button>
          <ActionHint className="caption">{TEXT_LABELS.APPLY_CHANGES_HINT}</ActionHint>
        </FlexboxContainer>
      </FlexboxContainer>
      <FlexboxContainer gap={Space.sp2x} alignItems="flex-start">
        <FlexboxContainer flexDirection="column" alignItems="center">
          <Button
            bgColor={canSendForApproval ? Main.brandDark : Main.disabled}
            borderColor={canSendForApproval ? Main.brandDark : Main.disabled}
            color={canSendForApproval ? Main.white : Shade.shade450}
            padding={`0 ${Space.sp3x}`}
            disabled={!canSendForApproval || !!isSending}
            onClick={onSendForApproval}
          >
            {TEXT_LABELS.SEND_FOR_APPROVAL_ACTION}
          </Button>
          {!canSendForApproval && <ActionHint className="caption">{TEXT_LABELS.SEND_FOR_APPROVAL_HINT}</ActionHint>}
        </FlexboxContainer>
        <FlexboxContainer gap={Space.spHalfPlus} alignItems="flex-start">
          <NoteIcon />
          <BuyerNote className="caption">{TEXT_LABELS.BUYER_NOTIFIED_HINT}</BuyerNote>
        </FlexboxContainer>
      </FlexboxContainer>
    </FooterCard>
  );
};

export default EditOrderFooter;
